import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ArrowUp, Check, CircleHelp, EyeOff, Loader2, Lock, Plane, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { useCachedAccess } from '@/lib/access/use-access';
import {
  AIRLINE_PROVIDER_SERVICES,
  AIRLINE_PROVIDER_VENDOR,
  airlineConnection,
  airlineRows,
  masterCanMove,
  masterSwitchState,
  masterTargets,
  pendingServices,
  serviceCapabilities,
  sharedReason,
  unreadServices,
  type AirlineConnection,
  type MasterSwitchState,
} from '@/lib/providers/airline-provider';
import { canonicalCategory, vendorLabel } from '@/lib/providers/integration-state';
import { hasLiveSwitch, IntegrationToggleError, TOGGLE_REASON_MAX } from '@/lib/providers/integration-toggle';
import type {
  AirlineCapabilityState,
  AirlineProviderService,
  AirlineProviderState,
  AirlineProviderSwitchResult,
  AirlineServiceSwitchOutcome,
  IntegrationDescriptor,
} from '@/lib/providers/types';
import { useAirlineProvider, useAirlineProviderSwitch } from '@/lib/providers/use-providers';
import { MODE_TONE } from './mode-tone';
import { SourceBadge } from '@/components/provenance/source-badge';
import { IntegrationSwitch } from './integration-toggle';
import { relativeTime } from './format';

/**
 * «PROVEEDOR DE LA AEROLÍNEA» — ONE card for the airline provider (contract
 * `airline-air-ticketing.v1` §0), instead of three unrelated rows in three service cards.
 *
 * Header: its name, whether it is connected (and in which mode) and the MASTER switch — on means
 * every service on, off every service off, and a mix is shown as what it is («Parcial · 2 de 3»)
 * and offers both: all off (the kill switch) or all on.
 * Body: one line per service it is switched under — flight information, passenger data, ticket
 * sales — with that service's own switch and what it gives Nexa right now, capability by
 * capability: available, or why not (no conectado · apagado · no lo ofrece · sin respuesta).
 *
 * Two sources, never mixed: connection and capabilities are the AIRLINE's answer; every switch is
 * the control plane's inventory row (the same switch the service cards used to show). The airline
 * reads the switch through a cache of seconds, so a service it has not caught up with says so
 * («todavía no aplica este cambio») and the card re-reads a bounded number of times — once those
 * are spent it stops promising another read and offers one instead. A switch the airline cannot
 * read at all keeps its service ON: said as that, never as a delay.
 *
 * Not connected: the card says so and shows no switch. Not reachable: UNKNOWN — said in a
 * sentence, never shown as "not connected"; the switches still work, since they are not the
 * airline's.
 */

/** The element id the service cards' airline rows scroll to. */
export const AIRLINE_PROVIDER_CARD_ID = 'airline-provider-card';

const CONNECTION_TONE: Record<AirlineConnection, string> = {
  connected: '',
  not_connected: 'border-border bg-muted/40 text-muted-foreground',
  unknown: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-300',
};

export function AirlineProviderCard({ integrations }: { integrations?: readonly IntegrationDescriptor[] | undefined }) {
  const { t } = useTranslation('network');
  const headingId = useId();
  const { query, catchingUp, recheck } = useAirlineProvider(integrations);
  // Lifted: the switch lives in the header, what it could not change is said under the services.
  const master = useAirlineProviderSwitch();
  const state = query.data;
  const rows = airlineRows(integrations);
  const name = vendorLabel(t, '', AIRLINE_PROVIDER_VENDOR);

  if (query.isLoading) {
    return <Skeleton className="h-40 w-full" data-testid="airline-provider-loading" />;
  }

  const connection = airlineConnection(state);
  // Not connected: nothing to switch. Unknown: the switches are the control plane's, still there.
  // No inventory on screen (unread or unassembled): no switch is claimed either way.
  const switches =
    integrations !== undefined &&
    (connection === 'connected' || (connection === 'unknown' && Object.keys(rows).length > 0));
  const pending = new Set(pendingServices(state, rows));
  const unread = new Set(unreadServices(state, rows));

  return (
    <Card
      id={AIRLINE_PROVIDER_CARD_ID}
      tabIndex={-1}
      className="overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      role="region"
      aria-labelledby={headingId}
      data-testid="airline-provider-card"
      data-connection={connection}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b px-4 py-3">
        <div className="min-w-0 flex-1 basis-72">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 id={headingId} className="flex items-center gap-2 text-base font-semibold">
              <Plane className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              {name}
            </h2>
            <ConnectionBadge connection={connection} state={state} />
            {state?.checkedAt ? (
              <span className="text-xs text-muted-foreground" data-testid="airline-provider-checked">
                {t('providers.airlineProvider.checked', { when: relativeTime(state.checkedAt, t) })}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('providers.airlineProvider.description')}</p>
        </div>
        {switches ? <MasterSwitch rows={rows} name={name} mutation={master} /> : null}
      </div>

      {connection === 'not_connected' ? (
        <p className="border-b bg-muted/40 px-4 py-2 text-sm text-muted-foreground" data-testid="airline-provider-not-connected">
          {t('providers.airlineProvider.notConnected')}
        </p>
      ) : null}
      {connection === 'unknown' ? (
        <p
          className="flex items-start gap-1.5 border-b bg-amber-500/5 px-4 py-2 text-sm text-amber-800 dark:text-amber-300"
          data-testid="airline-provider-unknown"
        >
          <CircleHelp className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {t('providers.airlineProvider.unknown')}
        </p>
      ) : null}

      <ul className="divide-y" aria-label={t('providers.airlineProvider.capabilities')}>
        {AIRLINE_PROVIDER_SERVICES.map((service) => (
          <ServiceLine
            key={service}
            service={service}
            state={state}
            row={rows[service]}
            showSwitch={switches}
            pending={pending.has(service)}
            catchingUp={catchingUp}
            onRecheck={recheck}
            unread={unread.has(service)}
          />
        ))}
      </ul>
      {switches ? <MasterOutcome result={master.data} /> : null}
    </Card>
  );
}

/** «Conectado · SANDBOX» · «No conectado» · «Sin respuesta». */
function ConnectionBadge({ connection, state }: { connection: AirlineConnection; state: AirlineProviderState | undefined }) {
  const { t } = useTranslation('network');
  const mode = connection === 'connected' ? state?.mode : undefined;
  const label =
    connection === 'connected'
      ? mode
        ? t('providers.airlineProvider.connection.connected', { mode: t(`providers.integrations.mode.${mode}`) })
        : t('providers.airlineProvider.connection.connectedPlain')
      : connection === 'not_connected'
        ? t('providers.airlineProvider.connection.notConnected')
        : t('providers.airlineProvider.connection.unknown');
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium',
        connection === 'connected' && mode ? MODE_TONE[mode] : CONNECTION_TONE[connection],
        connection === 'connected' && !mode && 'border-emerald-300 text-emerald-700 dark:text-emerald-400',
      )}
      data-testid="airline-provider-connection"
      data-state={connection}
      data-mode={mode}
    >
      {label}
    </span>
  );
}

/** One service: its name, what it gives Nexa now, and its own switch. */
function ServiceLine({
  service,
  state,
  row,
  showSwitch,
  pending,
  catchingUp,
  onRecheck,
  unread,
}: {
  service: AirlineProviderService;
  state: AirlineProviderState | undefined;
  row: IntegrationDescriptor | undefined;
  showSwitch: boolean;
  /** The airline still reads this switch the other way (its cache). */
  pending: boolean;
  /** A re-read is still coming; `false` once the bounded catch-up is spent. */
  catchingUp: boolean;
  onRecheck: () => void;
  /** The airline cannot read this switch and keeps the service on. */
  unread: boolean;
}) {
  const { t } = useTranslation('network');
  const capabilities = serviceCapabilities(state, service);
  const shared = sharedReason(capabilities);
  return (
    <li
      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2"
      data-testid={`airline-provider-service-${service}`}
      data-enabled={row ? String(row.enabled) : undefined}
      data-pending={pending || undefined}
    >
      <div className="min-w-0 flex-1 basis-80 space-y-0.5">
        <p className="text-sm font-medium">{t(`providers.airlineProvider.service.${service}`)}</p>
        <p className="text-xs text-muted-foreground" data-testid={`airline-provider-capabilities-${service}`}>
          {capabilities.length === 0 ? (
            '—'
          ) : shared ? (
            <SharedReasonLine t={t} capabilities={capabilities} reason={shared} service={service} />
          ) : (
            capabilities.map((c, i) => (
              <span key={c.capability}>
                {i > 0 ? ' · ' : null}
                <CapabilityChip t={t} capability={c} />
              </span>
            ))
          )}
        </p>
        {pending ? (
          <p
            className="flex flex-wrap items-start gap-x-1 text-xs text-amber-700 dark:text-amber-400"
            data-testid={`airline-provider-pending-${service}`}
            data-catching-up={catchingUp}
          >
            {catchingUp ? (
              <>
                <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin" aria-hidden />
                {t('providers.airlineProvider.pending')}
              </>
            ) : (
              <>
                <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                {t('providers.airlineProvider.pendingStale')}{' '}
                <button
                  type="button"
                  onClick={onRecheck}
                  className="inline-flex items-center gap-1 rounded-sm font-medium underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid={`airline-provider-recheck-${service}`}
                >
                  <RefreshCw className="h-3 w-3 shrink-0" aria-hidden />
                  {t('providers.airlineProvider.recheck')}
                </button>
              </>
            )}
          </p>
        ) : null}
        {unread && row ? (
          <p
            className={cn(
              'flex items-start gap-1 text-xs',
              // Switched off here but still on there: the switch is not doing what it says.
              row.enabled ? 'text-muted-foreground' : 'font-medium text-amber-700 dark:text-amber-400',
            )}
            data-testid={`airline-provider-unread-${service}`}
          >
            <EyeOff className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
            {t(`providers.airlineProvider.unread.${row.enabled ? 'on' : 'off'}`)}
          </p>
        ) : null}
      </div>
      {showSwitch ? (
        <div className="ml-auto shrink-0" data-testid={`airline-provider-switch-${service}`}>
          {row ? (
            <IntegrationSwitch
              descriptor={row}
              label={`${vendorLabel(t, service, AIRLINE_PROVIDER_VENDOR)} · ${t(`providers.airlineProvider.service.${service}`)}`}
            />
          ) : (
            <span className="text-xs text-muted-foreground">{t('providers.airlineProvider.noSwitch')}</span>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** «vuelos ✓», «PNR ✓ SINTÉTICO» or «señales — no lo ofrece». */
function CapabilityChip({ t, capability }: { t: TFunction<'network'>; capability: AirlineCapabilityState }) {
  const label = t(`providers.capabilityName.${capability.capability}`, { defaultValue: capability.capability });
  const synthetic = <SyntheticMark t={t} capability={capability} />;
  return (
    <span
      data-testid={`airline-provider-capability-${capability.capability}`}
      data-available={capability.available}
      data-reason={capability.reason}
      data-synthetic={capability.synthetic ? 'true' : undefined}
      className={cn(capability.available ? 'text-foreground' : 'text-muted-foreground')}
    >
      {capability.available ? (
        <>
          <Check className="mr-0.5 inline h-3 w-3 text-emerald-600" aria-hidden />
          {label}
          <span className="sr-only"> ({t('providers.airlineProvider.available')})</span>
          {synthetic}
        </>
      ) : (
        <>
          <span className="line-through decoration-muted-foreground/50">{label}</span>
          {synthetic}{' '}
          <span className={reasonTone(capability.reason)}>— {reasonText(t, capability.reason)}</span>
        </>
      )}
    </span>
  );
}

/**
 * Every capability of the service unavailable for ONE reason: the names, then the reason once. A
 * capability whose data is generated still says «Sintético» beside its name — switched off or not
 * answering, the data it serves when on is not the airline's.
 */
function SharedReasonLine({
  t,
  capabilities,
  reason,
  service,
}: {
  t: TFunction<'network'>;
  capabilities: readonly AirlineCapabilityState[];
  reason: AirlineCapabilityState['reason'];
  service: AirlineProviderService;
}) {
  return (
    <span data-testid={`airline-provider-shared-${service}`} data-reason={reason}>
      {capabilities.map((c, i) => (
        <span key={c.capability} data-synthetic={c.synthetic ? 'true' : undefined}>
          {i > 0 ? ' · ' : null}
          {t(`providers.capabilityName.${c.capability}`, { defaultValue: c.capability })}
          <SyntheticMark t={t} capability={c} />
        </span>
      ))}{' '}
      <span className={reasonTone(reason)}>— {reasonText(t, reason)}</span>
    </span>
  );
}

/**
 * « Sintético» after a capability's name when the provider declares its data generated (a mocked
 * adapter, demo only) — available or not, listed alone or in a shared-reason line. Nothing otherwise.
 */
function SyntheticMark({ t, capability }: { t: TFunction<'network'>; capability: AirlineCapabilityState }) {
  if (!capability.synthetic) return null;
  return (
    <>
      {' '}
      <SourceBadge
        source="SYNTHETIC"
        title={t('providers.airlineProvider.syntheticHint')}
        className="align-middle"
        testId={`airline-provider-synthetic-${capability.capability}`}
      />
    </>
  );
}

function reasonText(t: TFunction<'network'>, reason: AirlineCapabilityState['reason']): string {
  return t(`providers.airlineProvider.reason.${reason ?? 'upstream_unavailable'}`);
}

/** «sin respuesta» is the one reason that may be an outage — it reads amber. */
function reasonTone(reason: AirlineCapabilityState['reason']): string {
  return reason === 'upstream_unavailable' ? 'font-medium text-amber-700 dark:text-amber-400' : '';
}

/* ─────────────────────────────── Master switch ─────────────────────────────── */

const MASTER_PERMISSION = 'providers.toggle' as const;

/**
 * The provider as a whole. On → every service on; off → every service off; a mix reads «Parcial»
 * (a tri-state, `aria-checked="mixed"`) and pressing it offers BOTH «Apagar todo» and «Encender
 * todo» — from a mix it is still the kill switch. Like each service's switch: a reason is
 * required, nothing moves until the BFF answers, and without `providers.toggle` it is shown
 * read-only and says so. It is locked only when no service can move either way.
 */
function MasterSwitch({
  rows,
  name,
  mutation,
}: {
  rows: ReturnType<typeof airlineRows>;
  name: string;
  mutation: ReturnType<typeof useAirlineProviderSwitch>;
}) {
  const { t } = useTranslation('network');
  const canEdit = useCachedAccess().can(MASTER_PERMISSION);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const state = masterSwitchState(rows);
  if (state === 'none') return null;
  const targets = masterTargets(state);
  const movable = targets.some((target) => masterCanMove(rows, target));
  const mixed = targets.length > 1;
  const disabled = !canEdit || !movable || mutation.isPending;
  const switchable = Object.values(rows).filter((d) => d && hasLiveSwitch(d));
  const onCount = switchable.filter((d) => d?.enabled).length;
  const trimmed = reason.trim();
  const errorCode =
    mutation.error instanceof IntegrationToggleError ? mutation.error.code : mutation.error ? 'unavailable' : null;

  const openDialog = (): void => {
    mutation.reset();
    setReason('');
    setTouched(false);
    setOpen(true);
  };

  const confirm = (target: boolean): void => {
    setTouched(true);
    if (!trimmed) return;
    mutation.mutate({ enabled: target, reason: trimmed }, { onSuccess: () => setOpen(false) });
  };

  return (
    <div className="inline-flex flex-col items-end gap-0.5" data-testid="airline-provider-master">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {t('providers.airlineProvider.master.label')}
      </span>
      <button
        type="button"
        role="checkbox"
        aria-checked={state === 'mixed' ? 'mixed' : state === 'on'}
        aria-label={t('providers.airlineProvider.master.switchLabel', { name })}
        disabled={disabled}
        onClick={openDialog}
        data-testid="airline-provider-master-switch"
        data-state={state}
        className={cn(
          'inline-flex min-h-11 items-center gap-2 rounded-md px-1 text-xs',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <span aria-hidden className={cn('font-medium', MASTER_LABEL_TONE[state])}>
          {t(`providers.airlineProvider.master.${state}`)}
        </span>
        <MasterTrack state={state} />
      </button>
      {state === 'mixed' ? (
        <span className="text-[11px] text-muted-foreground" data-testid="airline-provider-master-mixed">
          {t('providers.airlineProvider.master.mixedHint', { on: onCount, total: switchable.length })}
        </span>
      ) : null}
      {!canEdit || !movable ? (
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="airline-provider-master-locked">
          <Lock className="h-3 w-3 shrink-0" aria-hidden />
          {canEdit ? t('providers.airlineProvider.master.locked') : t('providers.airlineProvider.master.readOnly')}
        </span>
      ) : null}

      <Dialog open={open} onOpenChange={(next) => (mutation.isPending ? undefined : setOpen(next))}>
        <DialogContent data-testid="airline-provider-master-dialog">
          <DialogHeader>
            <DialogTitle>
              {mixed
                ? t('providers.airlineProvider.master.dialog.titleMixed', { name })
                : targets[0]
                  ? t('providers.airlineProvider.master.dialog.titleOn', { name })
                  : t('providers.airlineProvider.master.dialog.titleOff', { name })}
            </DialogTitle>
            <DialogDescription>
              {mixed
                ? t('providers.airlineProvider.master.dialog.bodyMixed', { on: onCount, total: switchable.length })
                : targets[0]
                  ? t('providers.airlineProvider.master.dialog.bodyOn')
                  : t('providers.airlineProvider.master.dialog.bodyOff')}
            </DialogDescription>
          </DialogHeader>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('providers.integrations.toggle.dialog.reasonLabel')}</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={TOGGLE_REASON_MAX}
              rows={3}
              placeholder={t('providers.integrations.toggle.dialog.reasonPlaceholder')}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              data-testid="airline-provider-master-reason"
            />
          </label>
          {touched && !trimmed ? (
            <p className="text-xs text-destructive" data-testid="airline-provider-master-reason-required">
              {t('providers.integrations.toggle.dialog.reasonRequired')}
            </p>
          ) : null}
          {mixed
            ? targets
                .filter((target) => !masterCanMove(rows, target))
                .map((target) => (
                  <p
                    key={String(target)}
                    className="flex items-center gap-1 text-xs text-muted-foreground"
                    data-testid={`airline-provider-master-cannot-${target ? 'on' : 'off'}`}
                  >
                    <Lock className="h-3 w-3 shrink-0" aria-hidden />
                    {t(`providers.airlineProvider.master.dialog.${target ? 'cannotOn' : 'cannotOff'}`)}
                  </p>
                ))
            : null}
          {errorCode ? (
            <p className="text-xs text-destructive" role="alert" data-testid="airline-provider-master-error">
              {t(`providers.integrations.toggle.error.${errorCode}`, {
                defaultValue: t('providers.integrations.toggle.error.unavailable'),
              })}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" disabled={mutation.isPending} onClick={() => setOpen(false)}>
              {t('providers.integrations.toggle.dialog.cancel')}
            </Button>
            {targets.map((target) => {
              const saving = mutation.isPending && mutation.variables?.enabled === target;
              return (
                <Button
                  key={String(target)}
                  type="button"
                  variant={target ? 'default' : 'destructive'}
                  disabled={mutation.isPending || !masterCanMove(rows, target)}
                  onClick={() => confirm(target)}
                  data-testid={`airline-provider-master-confirm-${target ? 'on' : 'off'}`}
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                  {saving
                    ? t('providers.integrations.toggle.dialog.saving')
                    : target
                      ? t('providers.airlineProvider.master.dialog.confirmOn')
                      : t('providers.airlineProvider.master.dialog.confirmOff')}
                </Button>
              );
            })}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MASTER_LABEL_TONE: Record<Exclude<MasterSwitchState, 'none'>, string> = {
  on: 'text-emerald-700 dark:text-emerald-400',
  off: 'text-muted-foreground',
  mixed: 'text-amber-700 dark:text-amber-400',
};

/** The track: knob right (all on), left (all off), centred on amber (a mix). */
function MasterTrack({ state }: { state: Exclude<MasterSwitchState, 'none'> }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
        state === 'on' ? 'bg-emerald-600' : state === 'mixed' ? 'bg-amber-500' : 'bg-muted-foreground/30',
      )}
    >
      <span
        className={cn(
          'pointer-events-none block h-4 w-4 rounded-full bg-background shadow transition-transform',
          state === 'on' ? 'translate-x-[1.125rem]' : state === 'mixed' ? 'translate-x-2.5' : 'translate-x-0.5',
        )}
      />
    </span>
  );
}

/**
 * What the last master-switch action could NOT change, by service and reason — the switches
 * already show what it did. Kept until the next action, so it survives the dialog closing.
 */
function MasterOutcome({ result }: { result: AirlineProviderSwitchResult | undefined }) {
  const { t } = useTranslation('network');
  const failed = (result?.services ?? []).filter(
    (s): s is Extract<AirlineServiceSwitchOutcome, { ok: false }> => !s.ok,
  );
  if (failed.length === 0) return null;
  return (
    <div
      className="flex items-start gap-1.5 border-t bg-amber-500/5 px-4 py-2 text-xs text-amber-800 dark:text-amber-300"
      role="status"
      data-testid="airline-provider-master-partial"
    >
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <div>
        <p className="font-medium">{t('providers.airlineProvider.master.partial')}</p>
        <ul>
          {failed.map((s) => (
            <li key={s.service} data-testid={`airline-provider-master-partial-${s.service}`}>
              {t('providers.airlineProvider.master.partialItem', {
                service: t(`providers.airlineProvider.service.${s.service}`),
                reason: t(`providers.integrations.toggle.error.${toggleCode(s.error)}`),
              })}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** A refusal code the switch copy knows; anything else reads "could not apply". */
function toggleCode(error: string): string {
  return ['credentials_missing', 'last_provider', 'not_toggleable', 'version_conflict', 'unknown_vendor', 'invalid_request'].includes(
    error,
  )
    ? error
    : 'unavailable';
}

/**
 * A pressable pointer from a service card's airline row to this card — the switch lives HERE.
 * Scrolls the card into view and moves focus to it.
 */
export function AirlineProviderLink({ descriptor }: { descriptor: IntegrationDescriptor }) {
  const { t } = useTranslation('network');
  const name = vendorLabel(t, descriptor.category, descriptor.vendor);
  const go = (): void => {
    const card = document.getElementById(AIRLINE_PROVIDER_CARD_ID);
    card?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    card?.focus({ preventScroll: true });
  };
  return (
    <div className="inline-flex flex-col items-end">
      <span
        className={cn(
          'text-xs font-medium',
          descriptor.enabled ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
        )}
      >
        {descriptor.enabled ? t('providers.integrations.toggle.on') : t('providers.integrations.toggle.off')}
      </span>
      <button
        type="button"
        onClick={go}
        aria-label={t('providers.airlineProvider.manageInCardLabel', { name })}
        className="inline-flex min-h-8 items-center gap-1 rounded-sm text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`integration-airline-link-${canonicalCategory(descriptor.category)}`}
      >
        <ArrowUp className="h-3 w-3 shrink-0" aria-hidden />
        {t('providers.airlineProvider.manageInCard')}
      </button>
    </div>
  );
}
